/**
 * /lifetime — Lifetime Deal landing page (AppSumo-style FOMO).
 *
 * One-time €297 → unlocks every paid feature forever (Autopilot, AI
 * Intelligence, Brand Monitor, every future addon). Limited to 100 spots
 * total — `GET /api/payments/lifetime-status` returns live `sold` and
 * `remaining` counts which we use to drive the FOMO ticker. When all
 * spots are sold the CTA flips to a generic "Notify me" form (re-uses
 * the funnel-tools waitlist endpoint).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Crown, Loader2, Lock } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { API } from "../config";
import { getStoredAffCode } from "./AffiliateRedirect";
import { OrderNowBottomCTA } from "../components/common/ServiceOrderBundle";

const COPY = {
  sk: {
    eyebrow: "LIMITOVANÁ PONUKA · LEN 100 MIEST",
    h1: "Zaplať raz. Používaj navždy.",
    sub: "TopRankPulse Lifetime Access — všetky AI nástroje, všetky budúce funkcie, žiadne mesačné poplatky. Iba pre prvých 100 zákazníkov.",
    price_label: "Lifetime",
    price_currency: "€",
    monthly_alt: "namiesto €49/mes navždy",
    spots_left: "{n} miest zostáva",
    spots_sold: "{n}/100 obsadených",
    sold_out: "VYPREDANÉ",
    cta: "Získať Lifetime prístup",
    cta_busy: "Spracúvam...",
    notify_cta: "Upozorniť ma na ďalšie kolo",
    notify_done: "Hotovo, dáme vedieť!",
    bullets_h: "Čo dostaneš navždy:",
    bullets: [
      "Neobmedzené SEO/GEO/AI skeny",
      "AI Visibility tracking (ChatGPT, Perplexity, Gemini)",
      "Backlink Agent (vlastný, žiadny Ahrefs)",
      "AI Blog Agent — 25 článkov/mesiac",
      "SEO Autopilot + Brand Monitor + AI Intelligence",
      "PulseStudio Ads Creator (30 reklám/mes)",
      "Všetky budúce funkcie zadarmo",
      "Žiadne mesačné poplatky. Nikdy.",
    ],
    fine_print: "Jednorazová platba cez Stripe. 14-dňová záruka vrátenia peňazí. GDPR-friendly.",
    fomo_h: "Prečo iba 100?",
    fomo_p: "Lifetime Deal ďalej škáluje len pri prevádzkovej kapacite. Po obsadení 100 miest sa cena vráti späť na €49/mes.",
  },
  en: {
    eyebrow: "LIMITED OFFER · ONLY 100 SPOTS",
    h1: "Pay once. Use forever.",
    sub: "TopRankPulse Lifetime Access — every AI tool, every future feature, zero monthly fees. Only for the first 100 customers.",
    price_label: "Lifetime",
    price_currency: "€",
    monthly_alt: "instead of €49/mo forever",
    spots_left: "{n} spots left",
    spots_sold: "{n}/100 taken",
    sold_out: "SOLD OUT",
    cta: "Claim Lifetime Access",
    cta_busy: "Processing...",
    notify_cta: "Notify me about the next round",
    notify_done: "Done, we'll let you know!",
    bullets_h: "What you get forever:",
    bullets: [
      "Unlimited SEO/GEO/AI scans",
      "AI Visibility tracking (ChatGPT, Perplexity, Gemini)",
      "Backlink Agent (our own, no Ahrefs)",
      "AI Blog Agent — 25 articles/month",
      "SEO Autopilot + Brand Monitor + AI Intelligence",
      "PulseStudio Ads Creator (30 ads/mo)",
      "Every future feature free",
      "No monthly fees. Ever.",
    ],
    fine_print: "One-time Stripe payment. 14-day money-back guarantee. GDPR-friendly.",
    fomo_h: "Why only 100?",
    fomo_p: "Lifetime Deal scales further only when ops capacity catches up. Once the 100 spots are taken, pricing reverts to €49/mo.",
  },
};

function getT(lang) {
  return COPY[lang] || COPY.en;
}

export default function LifetimeDealPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const t = getT(language || "en");
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyDone, setNotifyDone] = useState(false);

  useEffect(() => {
    document.title = `${t.h1} · TopRankPulse`;
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await axios.get(`${API}/payments/lifetime-status`);
        if (!cancelled) setStatus(r.data);
      } catch { /* show baseline */ }
    };
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [t.h1]);

  const checkout = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: "lifetime_deal",
        origin_url: window.location.origin,
        user_email: user?.email || "",
        language: language || "en",
        aff_code: getStoredAffCode(),
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      console.error(e);
      alert("Checkout failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const notify = async (e) => {
    e.preventDefault();
    if (!notifyEmail || !/^\S+@\S+\.\S+$/.test(notifyEmail)) return;
    try {
      await axios.post(`${API}/funnel-tools/waitlist`, {
        tool_slug: "lifetime-deal",
        email: notifyEmail.trim(),
        language: language || "en",
      });
      setNotifyDone(true);
    } catch { /* swallow */ }
  };

  const remaining = status?.remaining ?? 100;
  const sold = status?.sold ?? 0;
  const soldOut = !!status?.sold_out;
  const price = status?.price_eur ?? 297;
  const progressPct = Math.min(100, Math.round((sold / 100) * 100));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="lifetime-deal-page">
      {!embed && (
        <header className="border-b border-zinc-800 py-4 px-6">
          <Link to="/" className="font-black text-lg font-['Cabinet_Grotesk']">TopRankPulse</Link>
        </header>
      )}

      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <p className="text-xs font-mono tracking-[0.3em] text-[#FFE600] mb-4 inline-flex items-center gap-2 px-3 py-1.5 border border-[#FFE600]/30 rounded-full"
             data-testid="lifetime-eyebrow">
            <Crown className="w-3.5 h-3.5" />
            {t.eyebrow}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] mb-4 leading-tight">
            {t.h1}
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto">{t.sub}</p>
        </div>

        {/* Price + CTA card */}
        <div className="bg-[#141414] border-2 border-[#FFE600]/40 p-8 sm:p-12 mb-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-6xl sm:text-7xl font-black font-['Cabinet_Grotesk'] text-[#FFE600]" data-testid="lifetime-price">
                  €{price}
                </span>
                <span className="text-base text-zinc-400">{t.price_label}</span>
              </div>
              <p className="text-sm text-zinc-500 mb-6">{t.monthly_alt}</p>

              {/* Spots ticker */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2 text-xs font-mono uppercase tracking-wider">
                  <span className={soldOut ? "text-red-400" : "text-[#10B981]"} data-testid="lifetime-remaining">
                    {soldOut ? t.sold_out : t.spots_left.replace("{n}", remaining)}
                  </span>
                  <span className="text-zinc-500" data-testid="lifetime-sold">
                    {t.spots_sold.replace("{n}", sold)}
                  </span>
                </div>
                <div className="h-2 bg-zinc-800 rounded overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#FFE600] to-[#FF3333] transition-all"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>

              {!soldOut ? (
                <button
                  type="button"
                  onClick={checkout}
                  disabled={loading}
                  className="w-full px-6 py-4 bg-[#FFE600] text-black font-black uppercase tracking-wider transition-opacity disabled:opacity-60 flex items-center justify-center gap-2 text-base"
                  data-testid="lifetime-cta"
                >
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> {t.cta_busy}</> : <>{t.cta} <ArrowRight className="w-5 h-5" /></>}
                </button>
              ) : (
                <form onSubmit={notify} className="space-y-2" data-testid="lifetime-notify-form">
                  {!notifyDone ? (
                    <>
                      <input
                        type="email"
                        required
                        value={notifyEmail}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                        placeholder="email@…"
                        className="w-full px-4 py-3 bg-[#0a0a0a] border-2 border-zinc-700 text-white focus:border-[#FFE600] focus:outline-none"
                      />
                      <button type="submit" className="w-full px-6 py-3 bg-zinc-800 text-white font-bold uppercase tracking-wider hover:bg-zinc-700">
                        <Lock className="w-4 h-4 inline mr-2" />{t.notify_cta}
                      </button>
                    </>
                  ) : (
                    <div className="px-4 py-3 bg-[#10B981]/10 border border-[#10B981]/40 text-[#10B981] text-sm font-bold text-center">
                      <Check className="w-4 h-4 inline mr-2" />{t.notify_done}
                    </div>
                  )}
                </form>
              )}
              <p className="text-xs text-zinc-500 mt-3 text-center">{t.fine_print}</p>
            </div>

            {/* Bullets */}
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#FFE600] mb-4">{t.bullets_h}</p>
              <ul className="space-y-3" data-testid="lifetime-bullets">
                {t.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <Check className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-200">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* FOMO explainer */}
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-lg font-black font-['Cabinet_Grotesk'] mb-2">{t.fomo_h}</h2>
          <p className="text-sm text-zinc-400">{t.fomo_p}</p>
        </div>

        <div className="max-w-5xl mx-auto mt-16">
          <OrderNowBottomCTA
            packageId="lifetime_deal"
            priceEur={297}
            interval="jednorazový"
            serviceName="TopRankPulse Lifetime"
            headline="Zabezpeč si dožitie prístup teraz"
            subcopy="Platíš raz €297 — používaš navždy. Žiadne mesačné poplatky. Limitované na 100 miest."
            testid="lifetime-final-cta"
          />
        </div>
      </section>
    </div>
  );
}
